import { useState } from 'react';
import { noop } from 'lodash';
import { logger } from '@fiverr-private/obs';
import { updateCollection } from '../services/updateCollection';
import { CollectionFormFields, RawCollection } from '../types';
import { raiseListSaved, raiseSaveListError } from '../components/toasts/toasts';
import { CollectionsContext } from '../context/CollectionsContext';
import { isCollectionKimeraError } from '../utils/isCollectionKimeraError';
import { sendListHasBeenEditedEvent } from '../utils/events/freelancersListEvents';
import { useOptionalQueryClient } from './useOptionalQueryClient';
import { useCollections } from './useCollections';

export interface UseSaveCollection {
    id?: string;
    bi: CollectionsContext['bi'];
    onSuccess?: (collection: RawCollection) => void;
    onError?: (error: Error) => void;
}
export const useSaveCollection = (options: UseSaveCollection) => {
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useOptionalQueryClient();
    const { collectionsById } = useCollections();
    const { onSuccess = noop, onError = noop, bi, id } = options;

    const saveCollection = async (collection: CollectionFormFields, collectionBeforeChanges: CollectionFormFields) => {
        if (!id) {
            return;
        }

        setIsLoading(true);

        try {
            await updateCollection(id, collection);
            onSuccess({ ...collection, id });
            setIsLoading(false);
            raiseListSaved();
            sendUpdatedEvents(id, collection, collectionBeforeChanges);

            queryClient?.invalidateQueries({ queryKey: ['collections'] });
        } catch (error) {
            setIsLoading(false);
            if (isCollectionKimeraError(error)) {
                raiseSaveListError(error.response?.data.errorKey);
            } else {
                raiseSaveListError();
                logger.error(error as Error, { description: 'Error while saving collection' });
            }
            onError(error as Error);
        }
    };

    const sendUpdatedEvents = (
        id: string,
        collection: CollectionFormFields,
        collectionBeforeChanges: CollectionFormFields
    ) => {
        const biProps = {
            biContext: bi,
            collection: {
                id,
                ...mapCollectionBiProps(collection),
            },
            collectionBeforeChanges: {
                id,
                ...mapCollectionBiProps(collectionBeforeChanges),
            },
            itemsCount: collectionsById[id]?.items.length,
        } satisfies Parameters<typeof sendListHasBeenEditedEvent>[0];

        sendListHasBeenEditedEvent(biProps);
    };

    const mapCollectionBiProps = (collection: CollectionFormFields) => {
        const { ownerType, name, description } = collection;
        return { type: ownerType, name, description };
    };

    return { isLoading, saveCollection };
};
